import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFetchURL } from "shared/config/setting";

import LogViewer from "shared/components/ui/LogViewer";
import { useUserData } from "shared/contexts/UserDataProvider";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import { getRobotEvent, ROBOT_EVENTS, useEventSubscription } from "shared/services/eventBus";
import RobotGroupsCard from "features/dashboard/pages/HomePage/robot-groups-card";

const UserRobotsDashBoard = () => {
  const { t } = useTranslation();
  const [connectionLogRecords, setConnectionLogRecords] = useState([]);

  const { error, robotListLoading: isPending, dispatch, robotGroups } = useUserData();

  const { connectionState, CONNECTION_STATES } = useServerConnection();

  const ready = connectionState === CONNECTION_STATES.CONNECTED;

  const addLogRecord = useCallback((msg) => {
    const time = new Date();
    setConnectionLogRecords((prev) => [...prev, { Time: time, Message: msg }]);
  }, []);

  const robotConnectedEventPath = getRobotEvent("*", ROBOT_EVENTS.CONNECTED);
  const robotDisconnectedEventPath = getRobotEvent("*", ROBOT_EVENTS.DISCONNECTED);
  const cnnPeersUpdatedEventPath = getRobotEvent("*", ROBOT_EVENTS.PEERS_UPDATED);
  const p2pEventPath = getRobotEvent("*", ROBOT_EVENTS.P2P_EVENT);

  useEventSubscription(
    robotConnectedEventPath,
    (evt) => {
      console.log("EVENT: robot connected event: ", evt);
      dispatch({ type: "SET_ROBOT_CONNECTED", payload: evt });
      addLogRecord("'" + evt.id + "' " + t('dashboard.robotConnected') + ". ");
    },
    {},
    [dispatch, t, addLogRecord]
  );

  useEventSubscription(
    robotDisconnectedEventPath,
    (evt) => {
      console.log("EVENT: robot disconnected event: ", evt);
      dispatch({ type: "SET_ROBOT_DISCONNECTED", payload: evt });
      addLogRecord("'" + evt.id + "' " + t('dashboard.robotDisconnected') + ". ");
    },
    {},
    [dispatch, t, addLogRecord]
  );

  useEventSubscription(
    cnnPeersUpdatedEventPath,
    (evt) => {
      console.log("EVENT: cnn_peers_updated event: ", evt);
      const robot_client_id = evt["robot_client_id"];
      const connecting_user_clients = evt["connecting_user_clients"] || [];
      const updated_data = { connecting_user_clients };
      dispatch({
        type: "UPDATE_ROBOT_CLIENT_DATA",
        payload: { robot_client_id, updated_data },
      });
      addLogRecord(
        "'" + robot_client_id + "' " + t('dashboard.peersUpdated') + ": " +
        JSON.stringify(updated_data)
      );
    },
    {},
    [dispatch, t, addLogRecord]
  );

  useEventSubscription(
    p2pEventPath,
    (evt) => {
      console.log("EVENT: peer2peer event: ", evt);
      const robot_client_id = evt["robot_client_id"];
      const user_client_id = evt["user_client_id"];
      const event_type = evt["event_type"];
      const event_data = evt["event_data"];
      const updated_data = evt["updated_data"] || {};
      dispatch({ type: "UPDATE_ROBOT_CLIENT_DATA", payload: { robot_client_id, updated_data } });
      addLogRecord(
        "'" + event_type + ": " + user_client_id + " <-> " + robot_client_id + "' " +
        JSON.stringify(event_data) + " updated_data: " + JSON.stringify(updated_data)
      );
    },
    {},
    [dispatch, t, addLogRecord]
  );

  const restart_robot = (robot) => {
    console.log("restart robot: ", robot);
    const robot_id = robot.id;

    fetch(getFetchURL("/restart-robot"), {
      body: JSON.stringify({
        cmd: "command",
        type: "restart_robot",
        robot_id: robot_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then((response) => {
        if (!response.ok)
          throw Error(t('dashboard.restartError') + " " + robot_id + '"');
        return response.json();
      })
      .then((data) => {
        if (data["result"] === "success") {
          alert(t('robots.title') + " " + robot_id + " " + t('dashboard.robotRestarting'));
          console.log("restarting robot_id: ", robot_id);

          // console.log("robots before delete:\n", robots);
          // const arr = robots.filter((rb) => {
          //   return rb.id !== robot_id;
          // });
          // setData(arr);
          // console.log("robots after delete:\n", arr);
        } else {
          alert(t('dashboard.restartResult') + ": " + data["result"] + "\n" + data["msg"]);
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") {
        } else {
          alert(err.message);
        }
      });
  };

  const shutdown_robot = (robot) => {
    console.log("shutdown robot: ", robot);
    const robot_id = robot.id;

    fetch(getFetchURL("/disconnect"), {
      body: JSON.stringify({
        cmd: "command",
        type: "shutdown_robot",
        robot_id: robot_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then((response) => {
        if (!response.ok)
          throw Error(t('dashboard.shutdownError') + " " + robot_id + '"');
        return response.json();
      })
      .then((data) => {
        if (data["result"] === "success") {
          dispatch({
            type: "SET_ROBOT_DISCONNECTED",
            payload: { id: robot_id },
          });
          alert(t('robots.title') + " " + robot_id + " " + t('dashboard.robotShutdown'));
          console.log("shutdown robot_id: ", robot_id);
        } else {
          alert(t('dashboard.shutdownResult') + ": " + data["result"] + "\n" + data["msg"]);
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") {
        } else {
          alert(err.message);
        }
      });
  };

  return (
    <div>
      {error && <div> {error}</div>}
      {isPending && <div>{t('common.loading')}</div>}
      <div>
        {t('dashboard.connectionStatus')}: {(ready && t('dashboard.ready')) || (!ready && t('dashboard.notReady'))}
      </div>
      {/* {robots && (
        <RobotsCard
          robots={robots}
          shutdownRobotClick={shutdown_robot}
          restartRobotClick={restart_robot}
          ShowOfflineRobots={true}
        ></RobotsCard>
      )} */}
      <RobotGroupsCard robotGroups={robotGroups}></RobotGroupsCard>
      {/* {robots && (
        <RobotsList
          robots={robots}
          shutdownRobotClick={shutdown_robot}
        ></RobotsList>
      )} */}
      <div>
        {t('dashboard.connectionEventLog')}:
        {/* {JSON.stringify(connectionLogRecords)} */}
      </div>
      <LogViewer LogRecords={connectionLogRecords}></LogViewer>
    </div>
  );
};

export default UserRobotsDashBoard;
